import { Badge } from '@/components/ui/badge';
import { ShieldCheck, ShieldAlert, Shield } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface DataConfidenceBadgeProps {
  /** BLS relative standard error of the mean wage (percent), when available. */
  relativeStandardError?: number | null;
  /** Total employment for the estimate - used as a sample-size proxy when PRSE is absent. */
  employment?: number | null;
  className?: string;
}

type Level = 'high' | 'medium' | 'low';

const meta: Record<Level, { label: string; className: string; icon: typeof ShieldCheck; tip: string }> = {
  high: {
    label: 'High confidence',
    className: 'bg-green-100 text-green-800 border-green-200',
    icon: ShieldCheck,
    tip: 'Based on a large sample with a low relative standard error. This estimate is reliable.',
  },
  medium: {
    label: 'Moderate confidence',
    className: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: Shield,
    tip: 'Based on a moderate sample. Treat this estimate as a reasonable guide.',
  },
  low: {
    label: 'Lower confidence',
    className: 'bg-amber-100 text-amber-800 border-amber-200',
    icon: ShieldAlert,
    tip: 'Smaller sample or higher variability. Use this estimate as a rough indication.',
  },
};

/**
 * Surfaces a data-quality signal that BLS publishes but most salary sites hide.
 * Prefers the relative standard error (PRSE) when present; otherwise derives a
 * level from the employment sample size.
 */
const DataConfidenceBadge = ({ relativeStandardError, employment, className = '' }: DataConfidenceBadgeProps) => {
  let level: Level;

  if (relativeStandardError != null) {
    level = relativeStandardError <= 5 ? 'high' : relativeStandardError <= 15 ? 'medium' : 'low';
  } else if (employment != null) {
    level = employment >= 50000 ? 'high' : employment >= 5000 ? 'medium' : 'low';
  } else {
    return null;
  }

  const { label, className: badgeClass, icon: Icon, tip } = meta[level];

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className={`${badgeClass} ${className}`}>
            <Icon className="h-3 w-3 mr-1" />
            {label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="text-sm">{tip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default DataConfidenceBadge;
